import {
    Card,
    Dropdown,
    Field,
    Label,
    mergeClasses,
    Option,
    ToolbarButton,
} from '@fluentui/react-components';
import { useAppDispatch, useSelector } from '../../../../../store/hooks';
import { useTranslation } from 'react-i18next';
import {
    PresetReportingDayRange,
    updateCustomRangeDates,
    setPresetRange,
    customReportingDayRangeValue,
    metricsDateFormat,
    setGroundTeamId,
} from '../../../../../store/features/reportingDashboard/reportingDashboardSlice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import NewReportDialogAction from '../../../NewReportDialogAction/NewReportDialogAction';
import { createNewReport } from '../../../../../store/features/reporting/reportingSlice';
import { useReportingDashboardToolbarStyles } from './ReportingDashboardToolbar.styles';
import { getDescriptiveRangeLabels } from '../reportingDashboardUtils';
import { formatDate } from '../../../../../i18n';
import { useDateRangeConstraints } from './ReportingDashboardToolbar.utils';
import { Add24Regular } from '@fluentui/react-icons';
import { useDialog } from '../../../../../hooks/useDialog';
import { getConnectToTeamsForReporting } from '../../../../../store/features/help/helpSlice';
import useReportingTeamItems from '../../../../../hooks/useReportingTeamItems';
import DateRangeFilter from '../../../../shared/DateRangeFilter/DateRangeFilter';
import TeamsFilterDropdown from '../../../../shared/TeamsFilterDropdown/TeamsFilterDropdown';

const presetTimeRanges: readonly PresetReportingDayRange[] = [
    1, 7, 14, 28,
] as const;

const ReportingDashboardToolbar: React.FC = () => {
    const styles = useReportingDashboardToolbarStyles();
    const {
        loadStatus,
        selectedDayRange,
        selectedRangeDates,
        filters: { groundTeamId },
    } = useSelector((x) => x.reportingDashboard);

    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(getConnectToTeamsForReporting());
    }, [dispatch]);

    const { reportingTeams, reportingTeamsStatus } = useSelector((x) => x.help);
    const { allTeamsOption, teams } = useReportingTeamItems(reportingTeams);

    const setGroundTeamIdCallback = useCallback(
        (id: string) => {
            dispatch(setGroundTeamId(id !== allTeamsOption.id ? id : null));
        },
        [allTeamsOption, dispatch]
    );

    useEffect(() => {
        if (reportingTeamsStatus !== 'idle' || groundTeamId.isLocked) {
            return;
        }

        setGroundTeamIdCallback(allTeamsOption.id);
    }, [
        reportingTeamsStatus,
        groundTeamId.isLocked,
        allTeamsOption,
        setGroundTeamIdCallback,
    ]);

    const { startDate, setStartDate, endDate, setEndDate, dateConstraints } =
        useDateRangeConstraints(
            new Date(selectedRangeDates.start),
            selectedRangeDates.end ? new Date(selectedRangeDates.end) : null
        );

    const [validationValid, setValidationValid] = useState(true);

    const customRangeValidationMessages = useMemo(
        () => ({
            startAfterEnd: t('ReportingDashboard_Error_StartBeforeEnd'),
            outOfRange: t('ReportingDashboard_Error_DateConstraints')
                .replace(
                    '{min}',
                    formatDate(dateConstraints.min, metricsDateFormat)
                )
                .replace(
                    '{max}',
                    formatDate(dateConstraints.max, metricsDateFormat)
                ),
            maxDaySpan: t('ReportingDashboard_Error_MaxWeekRange').replace(
                '{range}',
                '6'
            ),
        }),
        [dateConstraints.max, dateConstraints.min, t]
    );

    const customRangeFilter = (
        <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={(date) => setStartDate(date || null)}
            onEndDateChange={(date) => setEndDate(date || null)}
            startLabel={<Label>{t('Reporting_StartDate')}</Label>}
            endLabel={<Label>{t('Reporting_EndDate')}</Label>}
            startMaxDate={dateConstraints.max}
            endMinDate={dateConstraints.min}
            endMaxDate={dateConstraints.max}
            formatDate={(date) =>
                formatDate(date ?? new Date(), metricsDateFormat)
            }
            required
            validationConstraints={{
                min: dateConstraints.min,
                max: dateConstraints.max,
                maxDaySpan: 42,
                includeEndDate: true,
            }}
            validationMessages={customRangeValidationMessages}
            onValidationChange={setValidationValid}
        />
    );

    const newReportDialog = useDialog();

    const rangeSelectCallback = useCallback(
        (value: string) => {
            if (value === customReportingDayRangeValue) {
                dispatch(updateCustomRangeDates({}));
                setStartDate(new Date(selectedRangeDates.start));
                setEndDate(
                    selectedRangeDates.end
                        ? new Date(selectedRangeDates.end)
                        : null
                );
                return;
            }

            dispatch(
                setPresetRange(
                    Number.parseInt(value) as PresetReportingDayRange
                )
            );
        },
        [
            dispatch,
            setStartDate,
            setEndDate,
            selectedRangeDates.start,
            selectedRangeDates.end,
        ]
    );

    const onRangeSubmit = useCallback(() => {
        if (!validationValid || !startDate || !endDate) {
            return;
        }

        dispatch(
            updateCustomRangeDates({
                start: startDate.toISOString(),
                end: endDate.toISOString(),
            })
        );
    }, [validationValid, startDate, endDate, dispatch]);

    const selectedRangeLabel = useMemo(() => {
        return selectedDayRange === 'custom'
            ? t('ReportingDashboard_CustomRange')
            : getDescriptiveRangeLabels(selectedDayRange, t).current;
    }, [selectedDayRange, t]);

    const loadingFinished =
        loadStatus === 'idle' && reportingTeamsStatus === 'idle';

    return (
        <>
            <Card className={`${styles.filterCard}`}>
                <div className={mergeClasses(styles.column, 'left')}>
                    <div className={styles.row}>
                        <Field
                            label={
                                <Label weight="semibold">
                                    {t('ReportingDashboard_ChooseTimePeriod')}
                                </Label>
                            }
                        >
                            <Dropdown
                                value={selectedRangeLabel}
                                selectedOptions={[selectedDayRange.toString()]}
                                onOptionSelect={(_, data) =>
                                    rangeSelectCallback(data.optionValue!)
                                }
                                disabled={!loadingFinished}
                            >
                                {presetTimeRanges.map((range) => {
                                    const rangeAsString = range.toString();

                                    const label =
                                        range > 1
                                            ? t(
                                                  'ReportingDashboard_CurrentNDays'
                                              ).replace(
                                                  '{count}',
                                                  rangeAsString
                                              )
                                            : t(
                                                  'ReportingDashboard_CurrentDay'
                                              );

                                    return (
                                        <Option
                                            key={range}
                                            value={rangeAsString}
                                        >
                                            {label}
                                        </Option>
                                    );
                                })}
                                <Option
                                    key={customReportingDayRangeValue}
                                    value={customReportingDayRangeValue}
                                >
                                    {t('ReportingDashboard_CustomRange')}
                                </Option>
                            </Dropdown>
                        </Field>
                        {selectedDayRange === customReportingDayRangeValue && (
                            <>
                                {customRangeFilter}
                                <ToolbarButton
                                    appearance="primary"
                                    onClick={onRangeSubmit}
                                    disabled={!validationValid}
                                >
                                    {t('ReportingDashboard_Load')}
                                </ToolbarButton>
                            </>
                        )}
                        <TeamsFilterDropdown
                            teams={teams}
                            allTeamsOption={allTeamsOption}
                            selectedTeamId={groundTeamId.value || null}
                            onTeamChange={setGroundTeamIdCallback}
                            teamLabel={
                                <Label weight="semibold">
                                    {t('ReportingDashboard_ChooseTeam')}
                                </Label>
                            }
                            teamPlaceholder={t('ReportingDashboard_ChooseTeam')}
                            dropdownAppearance="underline"
                            disabled={groundTeamId.isLocked}
                        />
                    </div>
                </div>
                <div className={styles.column}>
                    <ToolbarButton
                        appearance="subtle"
                        icon={<Add24Regular />}
                        onClick={newReportDialog.openDialog}
                    >
                        {t('ToolbarButton_GenerateReport')}
                    </ToolbarButton>
                </div>
            </Card>
            <NewReportDialogAction
                open={newReportDialog.isOpen}
                onOpenChange={(_, data) =>
                    newReportDialog.onOpenChangeCallback(data.open)
                }
                onSubmit={(_, data) => {
                    dispatch(createNewReport(data));
                }}
            />
        </>
    );
};

export default ReportingDashboardToolbar;
